<template>
	<div class="content-page content-page--vendors-history">
		<ActionSetNav
			v-if="integrator"
			:name="integrator.integratorName"
			:display="true"
			:actions="mainNavActions"
			ref="vendorsHistoryNav"
		/>
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					History
					<span v-if="integrator" class="content-page__title--detail">
						- {{ integrator.integratorName }}
					</span>
				</h2>
			</div>
		</div>
		<div class="results-toolbar">
			<VendorsHistoryFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:userOptions="users || []"
			/>
			<div class="results-toolbar__counter" v-if="resultCounter">
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>
		<Spinner :processor="{ loading: true }" v-if="loading" />
		<ScrollContainer v-if="!loading && hasResults">
			<Table
				class="table-container--vendors-history"
				:sourceObject="tableSource"
				:isArrowUp="true"
				:sortOptions="handleSort"
				@change="updatedPagination"
				@sort="updateDirection"
			>
				<template #col-group>
					<colgroup>
						<!-- Date/Time -->
						<col class="table__col table__col--date-time" />

						<!-- Action -->
						<col class="table__col table__col--action" />

						<!-- Changed By -->
						<col class="table__col table__col--changed-by" />

						<!-- Role -->
						<col class="table__col table__col--role" />
					</colgroup>
				</template>
				<template #body-timestamp="{ body }">
					<div class="vendors-history-data">
						{{ fullDateString(body.timestamp) }}
						<div class="vendors-history-data__text-emphasis">
							{{ differenceDateString(body.timestamp) }}
						</div>
					</div>
				</template>
				<template #body-action="{ body }">
					<span
						class="formHistoy-log-text"
						v-html="formatActionText(body, 1)"
					></span>
					<Button
						className="btn--link"
						labelText="Details"
						data-testid="rowDetailsLink"
						v-if="body.fieldChangesCount > 1"
						:btnClick="() => showRowDetail(body)"
					/>
					<Button
						v-if="isDeleteOrCreate(body)"
						className="btn--link"
						labelText="Details"
						:btnClick="() => displayDetailsModal(body)"
					/>
				</template>
			</Table>
			<LogHistoryDetailModal
				v-if="displayRowDetail"
				@close="displayRowDetail = false"
				title="History Log Form"
				:logs="sourceRowDetail"
				data-test="logHistoryDetailModal"
			/>
			<LogHistoryObjDetailModal
				:objectDetails="objectDetails"
				v-if="showObjDetailsModal"
				@close="showObjDetailsModal = false"
				data-test="logHistoryObjDetailModal"
			/>
		</ScrollContainer>
		<NoResults v-show="!hasResults && !loading" data-test="noResults" />
	</div>
</template>

<script>
import ActionSetNav from '@/site-components/actions-set/action-set-nav';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import Button from '@/components/forms/button/button';
import NoResults from '@/site-components/no-results/no-results';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Spinner from '@/components/visual/spinner/spinner';
import Table from '@/components/shells/table/table';
import VendorsHistoryFilters from './vendors-history--filters';
import LogHistoryDetailModal from './log-history-detail-modal';
import LogHistoryObjDetailModal from '@/views/shared/log-history-obj-detail-modal';

// vuex
import { mapActions, mapState } from 'vuex';

// libraries
import PageKeyNames from '@/utils/page-key-names';
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';
import _ from 'lodash';

// mixins
//;
import HistoryHelpers from '@/mixins/history-helpers';
import VendorsActions from '../summary/vendors-actions';
import DateTimeMixin from '@/mixins/datetime-mixin';

//constants
// const entityTypes = ['Integrators', 'IntegratorsContactsTypeModel'];
export default {
	name: 'VendorsHistory',
	mixins: [HistoryHelpers, VendorsActions, DateTimeMixin],
	components: {
		ActionSetNav,
		ScrollContainer,
		VendorsHistoryFilters,
		Breadcrumbs,
		Button,
		LogHistoryDetailModal,
		LogHistoryObjDetailModal,
		ResultCounter,
		NoResults,
		Spinner,
		Table
	},
	props: {
		orderBy: String,
		orderDirection: String,
		search: String,
		minDatetime: String,
		maxDatetime: String,
		changedByUserId: String,
		changedEntityId: [String, Number],
		page: Number,
		countPerPage: Number
	},
	data() {
		return {
			filters: {
				orderBy: this.orderBy,
				orderDirection: this.orderDirection,
				search: this.search,
				dateRange: {
					// object with date obj
					minDatetime: this.minDatetime,
					maxDatetime: this.maxDatetime
				},
				changedByUserId: this.changedByUserId,
				SourceApplication: 'VueSupport',
				entityType: 'Integrators',
				changedEntityId: this.changedEntityId,
				page: this.page,
				countPerPage: this.countPerPage
			},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: '',
				dateRange: {
					// object with date obj
					minDatetime: formatDate(
						new Date(sub(new Date(), { weeks: 1 }))
					),
					maxDatetime: formatDate(new Date())
				},
				changedByUserId: undefined,
				SourceApplication: 'VueSupport',
				entityType: 'Integrators',
				changedEntityId: this.$route.params.changedEntityId,
				page: 1,
				countPerPage: 10
			},
			vendorsLogs: [],
			loading: false,
			sourceRowDetail: {},
			displayRowDetail: false,
			urlNames: [
				{
					url: '/vendors',
					name: '3P Vendors'
				},
				{ name: 'Vendors History' }
			],
			headerOptions: [
				{
					key: 'timestamp',
					label: 'Date/Time',
					sorteable: true
				},
				{
					key: 'action',
					label: 'Action',
					sorteable: false
				},
				{
					key: 'changedByUserName',
					label: 'Changed By',
					sorteable: true
				},
				{
					key: 'changedByUserRole',
					label: 'Role',
					sorteable: false
				}
			]
		};
	},
	async created() {
		this.mainNavActions = this.actions;
		this.tableSource.headers = this.headerOptions;

		this.loading = true;
		try {
			await this.setVendor(this.changedEntityId);
			await this.getUsers({
				changedEntityId: this.changedEntityId,
				entityType: 'FormRepositoryModel'
			});
			this.vendor = this.integrator;
		} finally {
			this.loading = false;
		}
	},
	mounted() {
		this.$store.commit('setCurrentPage', PageKeyNames.VENDORS_HISTORY);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	computed: {
		...mapState('integrators', ['integrator']),
		...mapState('historyLogs', ['historyLogs', 'users']),
		hasResults() {
			return this.historyLogs.items?.length > 0;
		},
		resultCounter() {
			return this.historyLogs.items?.length || 0;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		}
	},
	methods: {
		...mapActions('integrators', ['setVendor']),
		...mapActions('historyLogs', ['getHistoryLogs', 'getUsers']),
		showRowDetail(log) {
			this.sourceRowDetail = log;
			this.displayRowDetail = true;
		},
		renderLogs() {
			let results = _.cloneDeep(this.logs);

			if (_.isEmpty(results)) return results;

			results.items.forEach(item => {
				let { fieldChanges } = item;
				item.fieldChangesCount = fieldChanges.items.length;
			});

			this.tableSource.source = results;
		},
		async fetchData() {
			this.loading = true;

			const filteredQuery = _.cloneDeep(this.filters);

			filteredQuery.recordStart =
				parseInt(
					(this.filters.page - 1) * this.filters.countPerPage + 1
				) || 1;
			filteredQuery.maxCount = this.filters.countPerPage;

			delete filteredQuery.page;
			delete filteredQuery.countPerPage;

			filteredQuery.minDatetime = this.filters.dateRange.minDatetime;
			filteredQuery.maxDatetime = this.filters.dateRange.maxDatetime;

			delete filteredQuery.dateRange;

			try {
				await this.getHistoryLogs(filteredQuery);

				this.vendorsLogs = [...this.historyLogs.items];
				this.tableSource.source = this.historyLogs;
			} finally {
				this.loading = false;
			}
		},
		updateRoute(queries) {
			this.$router
				.push({
					path: `/vendors/${this.$route.params.changedEntityId}/history`,
					query: {
						orderBy: queries.orderBy || this.defaultFilters.orderBy,
						orderDirection:
							queries.orderDirection ||
							this.defaultFilters.orderDirection,
						search: queries.search || undefined,
						minDatetime:
							formatDate(queries.dateRange.minDatetime) ||
							formatDate(
								this.defaultFilters.dateRange.minDatetime
							),
						maxDatetime:
							formatDate(queries.dateRange.maxDatetime) ||
							formatDate(
								this.defaultFilters.dateRange.maxDatetime
							),
						changedByUserId: queries.changedByUserId || undefined
					}
				})
				.catch(() => {});

			this.fetchData();
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
		},
		updatedPagination(value) {
			this.filters.page = value.page;
			this.filters.countPerPage = value.pageSize;
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(newValue) {
				this.updateRoute(newValue);
			}
		},
		logs: {
			deep: true,
			handler() {
				this.renderLogs();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * [Description]
    *
    * Vendors History Screen
    * Can use SCSS variables = TRUE
    *
**/
::v-deep .results-toolbar__search {
	@include mq($from: md) {
		width: 250px;
	}
}
</style>

<style lang="scss">
/*------------------------------------*\
  Vendors History Table
\*------------------------------------*/

.table-container--vendors-history {
	.table {
		@include mq($from: md) {
			table-layout: fixed;
		}
	}
	.table__col {
		@include mq($from: md) {
			&--date-time,
			&--role,
			&--changed-by {
				width: 85px;
			}
			&--action {
				width: 175px;
			}
		}
	}
}

/*------------------------------------*\
  Vendors History Table - Data
\*------------------------------------*/

.vendors-history-data {
	&__text-emphasis {
		@include body-text('regular-emphasis');
		margin-top: $gutter-sm;
	}

	&__action {
		display: flex;
		justify-content: space-between;
		gap: $gutter-xl;
		align-items: center;

		&-text {
			text-transform: initial;
		}
	}
}
</style>
